import type { CourseId, Mode, Surface, ZoneId } from "../config/types";

type Empty = Record<string, never>;

export interface GameEvents {
    runStart: { mode: Mode; course: CourseId | null; seed: number };
    runEnd: { reason: "caught" | "crash" | "finished" | "escaped" | "timeout" | "quit" };
    wipeout: { severity: "small" | "medium" | "large"; cause: string };
    recover: Empty;
    boostOn: { amount?: number };
    boostOff: { amount?: number };
    boostRefill: { amount?: number };
    jump: { source: "ramp" | "bunnyhop" | "roller" | "lip" | "drop"; strength: number; pop: boolean };
    skinnyStart: { lengthM: number; clean: boolean };
    skinnyEnd: { lengthM: number; clean: boolean };
    skid: { on: boolean };
    surfaceChange: { surface: Surface };
    zoneChange: { zone: ZoneId };
    finishLine: { timeS: number };
    trickStart: { trick: string };
    trickComplete: { trick: string; value: number };
    land: { clean: boolean; airtime: number };
    trickFail: { trick: string };
    gatePassed: { index: number };
    gateMissed: { index: number };
    pickup: { type: string };
    bigfootWarning: Empty;
    bigfootSpawn: Empty;
    bigfootCaught: Empty;
    originShift: { dy: number };
    pause: Empty;
    resume: Empty;
}

type EventName = keyof GameEvents;
type Listener<E extends EventName> = (payload: GameEvents[E]) => void;

const listeners = new Map<EventName, Set<Listener<any>>>();

export function on<E extends EventName>(name: E, fn: Listener<E>): () => void {
    if (!listeners.has(name)) listeners.set(name, new Set());
    listeners.get(name)!.add(fn);
    return () => listeners.get(name)?.delete(fn);
}

export function emit<E extends EventName>(name: E, payload: GameEvents[E]) {
    listeners.get(name)?.forEach((fn) => fn(payload));
}

export function clearAll() {
    listeners.clear();
}
